import type { ReactNode } from 'react'
import { AlertCircle, RefreshCw } from 'lucide-react'
import { cn } from '@/lib/cn'
import { Text } from './Text'
import { Button } from './Button'
import { Spinner } from './Spinner'

// ── EmptyState ────────────────────────────────────────────────────────────────

export interface EmptyStateProps {
  icon?: ReactNode
  title: string
  description?: string
  /** Any ReactNode — Button, link-style button, or custom CTA. */
  action?: ReactNode
  className?: string
  /**
   * Overrides the description's width cap (default `max-w-xs`). Optional and additive — for a
   * caller whose supporting copy runs to a full sentence or two, which `max-w-xs` would wrap into a
   * tall, narrow column (e.g. `ComingSoon` on a full-page stub).
   */
  descriptionClassName?: string
}

export function EmptyState({ icon, title, description, action, className, descriptionClassName }: EmptyStateProps) {
  return (
    <div className={cn('flex flex-col items-center text-center', className)}>
      {icon && (
        // Two concentric circles instead of a flat icon box — a soft outer halo (the one
        // deliberate flourish) around a solid inner disc the same 40px the icon sat in before,
        // so every existing caller's icon keeps the footprint it was sized for. Same two tokens
        // the flat box already used (bg-surface-muted, text-text-subtle), just layered for depth
        // — no new color, no shadow, no gradient, so it reads right across all 40+ call sites.
        <div className="relative grid place-items-center w-16 h-16 mb-4">
          <div className="absolute inset-0 rounded-full bg-surface-muted/60" />
          <div className="absolute inset-3 rounded-full bg-surface-muted" />
          <div className="relative text-text-subtle">{icon}</div>
        </div>
      )}
      <Text variant="title-md" as="h3">
        {title}
      </Text>
      {description && (
        <Text variant="body-sm" color="muted" className={cn('mt-1 max-w-xs', descriptionClassName)}>
          {description}
        </Text>
      )}
      {action && <div className="mt-4">{action}</div>}
    </div>
  )
}

// ── ErrorState ────────────────────────────────────────────────────────────────

export interface ErrorStateProps {
  title?: string
  description?: string
  onRetry?: () => void
  className?: string
}

export function ErrorState({
  title = 'Something went wrong',
  description = "We couldn't load this. Check your connection and try again.",
  onRetry,
  className,
}: ErrorStateProps) {
  return (
    <div role="alert" className={cn('flex flex-col items-center text-center', className)}>
      <div className="grid place-items-center w-10 h-10 rounded-full bg-danger-subtle text-danger mb-3">
        <AlertCircle size={18} aria-hidden="true" />
      </div>
      <Text variant="title-md" as="h3">
        {title}
      </Text>
      <Text variant="body-sm" color="muted" className="mt-1 max-w-xs">
        {description}
      </Text>
      {onRetry && (
        <Button variant="outline" size="sm" className="mt-4 gap-2" onClick={onRetry}>
          <RefreshCw size={12} />
          Try again
        </Button>
      )}
    </div>
  )
}

// ── LoadingState ──────────────────────────────────────────────────────────────

export interface LoadingStateProps {
  label?: string
  className?: string
}

export function LoadingState({ label, className }: LoadingStateProps) {
  return (
    <div className={cn('flex items-center justify-center gap-2', className)}>
      <Spinner size="sm" className="text-primary" />
      {label && (
        <Text variant="body-sm" color="muted" as="span">
          {label}
        </Text>
      )}
    </div>
  )
}
